/**
 * Purchase Summary Report Table Component
 * @see Sprint 32 - Rapport Sommaire des Achats
 * @see US-32.3 - Frontend Page et Composants
 *
 * Uses DataGrid with:
 * - Grouping by supplier with subtotals
 * - Grand total row
 * - Export Excel with native dataType formatting
 */

import { useMemo } from 'react';
import {
  DataGrid,
  textColumn,
  dateColumn,
  integerColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridGrouping,
  type DataGridSummaryItem,
} from '@bill/ui';
import { buildExportFileName } from '@/lib/export-filename';
import { useCurrency, useBrandContext } from '@/hooks';
import type { PurchaseSummaryRow } from '@bstock/shared';

interface PurchaseSummaryTableProps {
  rows: PurchaseSummaryRow[];
  isLoading: boolean;
  onRowClick?: (row: PurchaseSummaryRow) => void;
  dateFrom?: string;
  dateTo?: string;
}

export function PurchaseSummaryTable({
  rows,
  isLoading,
  onRowClick,
  dateFrom,
  dateTo,
}: PurchaseSummaryTableProps) {
  const currency = useCurrency();
  const { brand } = useBrandContext();

  const exportFileName = useMemo(() =>
    buildExportFileName('rapport_achats', brand?.name ?? 'export', dateFrom, dateTo),
  [brand?.name, dateFrom, dateTo]);

  const columns = useMemo<DataGridColumn<PurchaseSummaryRow>[]>(() => [
    dateColumn<PurchaseSummaryRow>({
      id: 'createdAt',
      header: 'Creation',
      accessor: (r) => r.createdAt,
      exportValue: (r) => r.createdAt,
    }),
    dateColumn<PurchaseSummaryRow>({
      id: 'receiptDate',
      header: "Date d'effet",
      accessor: (r) => r.receiptDate,
      exportValue: (r) => r.receiptDate,
    }),
    textColumn<PurchaseSummaryRow>({
      id: 'receiptNumber',
      header: 'N\u00b0 Reception',
      sortable: false,
      accessor: 'receiptNumber',
    }),
    textColumn<PurchaseSummaryRow>({
      id: 'supplierInvoiceRef',
      header: 'N\u00b0 Facture',
      sortable: false,
      accessor: (r) => r.supplierInvoiceRef ?? '-',
    }),
    integerColumn<PurchaseSummaryRow>({
      id: 'itemCount',
      header: 'Nb articles',
      sortable: false,
      accessor: 'itemCount',
    }),
    moneyColumn<PurchaseSummaryRow>({
      id: 'totalExclTaxCentimes',
      header: 'Total HT',
      accessor: (r) => r.totalExclTaxCentimes / 100,
      sortable: false,
    }),
    moneyColumn<PurchaseSummaryRow>({
      id: 'vatAmountCentimes',
      header: 'TVA',
      accessor: (r) => r.vatAmountCentimes / 100,
      sortable: false,
    }),
    moneyColumn<PurchaseSummaryRow>({
      id: 'totalInclTaxCentimes',
      header: 'Total TTC',
      accessor: (r) => r.totalInclTaxCentimes / 100,
      sortable: false,
    }),
  ], []);

  const grouping = useMemo<DataGridGrouping<PurchaseSummaryRow>>(() => ({
    groupBy: 'supplierName',
    defaultExpanded: 'all',
    ungroupedLabel: 'Fournisseur inconnu',
  }), []);

  const summary = useMemo<DataGridSummaryItem<PurchaseSummaryRow>[]>(() => [
    {
      columnId: 'itemCount',
      aggregate: 'sum',
      value: (r) => r.itemCount,
    },
    {
      columnId: 'totalExclTaxCentimes',
      aggregate: 'sum',
      value: (r) => r.totalExclTaxCentimes / 100,
    },
    {
      columnId: 'vatAmountCentimes',
      aggregate: 'sum',
      value: (r) => r.vatAmountCentimes / 100,
    },
    {
      columnId: 'totalInclTaxCentimes',
      aggregate: 'sum',
      value: (r) => r.totalInclTaxCentimes / 100,
    },
  ], []);

  return (
    <DataGrid<PurchaseSummaryRow>
      data={rows}
      columns={columns}
      rowKey={(r) => r.goodsReceiptId}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={onRowClick}
      grouping={grouping}
      summary={summary}
      showGrandTotal
      currency={currency}
      maxHeight="80vh"
      exportable={{
        fileName: exportFileName,
        sheetName: 'Rapport Achats',
        position: 'toolbar',
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune reception pour cette periode
        </div>
      }
    />
  );
}
